import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Sparkles, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "@/App";

/**
 * "This Week's Contest" banner — auto-populates from /api/contests/active.
 * Renders nothing if there's no active contest.
 */
export default function WeeklyContestBanner() {
  const navigate = useNavigate();
  const [contest, setContest] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/contests/active`, { credentials: "include" });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const first = Array.isArray(data) ? data[0] : (data?.contests?.[0] || null);
        if (first) setContest(first);
      } catch (e) {
        // banner is optional — silent failure is fine
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!contest || dismissed) return null;

  const prizePool =
    (contest.prize_first || 0) +
    (contest.prize_second || 0) +
    (contest.prize_third || 0);

  // days remaining
  let endLabel = "";
  if (contest.end_at) {
    const ms = new Date(contest.end_at).getTime() - Date.now();
    if (ms > 0) {
      const days = Math.floor(ms / (1000 * 60 * 60 * 24));
      const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      endLabel = days > 0 ? `${days}d ${hours}h left` : `${hours}h left`;
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        className="brutal-card mb-3 p-3 cursor-pointer relative overflow-hidden"
        style={{
          background: "linear-gradient(90deg, rgba(255,0,255,0.25), rgba(0,255,255,0.15), rgba(255,215,0,0.25))",
          borderColor: "#FFD700",
        }}
        onClick={() => navigate(`/contests/${contest.contest_id}`)}
        data-testid="weekly-contest-banner"
      >
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setDismissed(true); }}
          className="absolute top-1 right-1 text-[#FFD700] text-xs hover:text-white"
          data-testid="weekly-contest-dismiss"
          aria-label="Dismiss"
        >
          ✕
        </button>
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#FFD700] flex items-center justify-center">
            <Trophy className="w-6 h-6 text-[#000033]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider text-[#00FFFF]">
              <Sparkles className="w-3 h-3" />
              This week's contest
              {endLabel && <span className="ml-1 text-[#FF66FF]">· {endLabel}</span>}
            </div>
            <h3
              className="font-bold text-[#FFD700] text-base leading-tight truncate"
              data-testid="weekly-contest-title"
            >
              {contest.title}
            </h3>
            <p className="text-xs text-[#FFFFFF] truncate">
              {contest.theme || contest.description}
            </p>
          </div>
          <div className="flex flex-col items-end flex-shrink-0">
            <div className="text-xs text-[#00FFFF] font-bold leading-none" data-testid="weekly-contest-prize">
              🪙 {prizePool}
            </div>
            <div className="text-[10px] text-[#FF66FF]">prize pool</div>
            <div className="flex items-center gap-0.5 mt-1 text-[#FFD700] text-xs font-bold">
              Enter <ChevronRight className="w-3 h-3" />
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
